// src/ui/components/dialogs/FullscreenDialog.hooks.ts
import { useEffect, useState } from 'react';
import { resetMediaElements } from './FullscreenDialog.utils';

/**
 * Hook para manejar el lazy loading de media
 */
export const useLazyMedia = (showExpanded?: boolean, showFullScreen?: boolean) => {
  const [shouldLoadMedia, setShouldLoadMedia] = useState(false);

  useEffect(() => {
    if (showExpanded || showFullScreen) {
      setShouldLoadMedia(true);
    } else {
      setShouldLoadMedia(false);
    }
  }, [showExpanded, showFullScreen]);

  return shouldLoadMedia;
};

/**
 * Hook para manejar el scroll del body y reset de media
 */
export const useBodyScrollLock = (
  showExpanded?: boolean,
  showFullScreen?: boolean,
  dialogRef?: React.RefObject<HTMLDialogElement | null>,
) => {
  useEffect(() => {
    if (showExpanded || showFullScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';

      // Resetear media cuando se cierra
      if (dialogRef?.current) {
        resetMediaElements(dialogRef.current);
      }
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showExpanded, showFullScreen, dialogRef]);
};

/**
 * Hook para manejar eventos de cierre del diálogo
 */
export const useDialogCloseEvents = (
  supportsDialog: boolean,
  dialogRef: React.RefObject<HTMLDialogElement | null>,
  showExpanded?: boolean,
  showFullScreen?: boolean,
) => {
  useEffect(() => {
    if (!supportsDialog) return;
    const d = dialogRef.current;
    if (!d) return;

    const onDialogClose = () => {
      const mode = showFullScreen ? 'fullscreen' : showExpanded ? 'expanded' : 'none';
      window.dispatchEvent(
        new CustomEvent('fullscreen-dialog-closed', {
          detail: { mode },
        }),
      );
    };

    d.addEventListener('close', onDialogClose);
    return () => {
      d.removeEventListener('close', onDialogClose);
    };
  }, [supportsDialog, showExpanded, showFullScreen, dialogRef]);
};

/**
 * Hook para manejar requests de cierre desde otras instancias
 */
export const useDialogRequestClose = (
  supportsDialog: boolean,
  dialogRef: React.RefObject<HTMLDialogElement | null>,
  idRef: React.MutableRefObject<string>,
  internalSwitchingRef: React.MutableRefObject<boolean>,
  onRequestClose?: () => void,
) => {
  // Listener para cuando hay soporte de dialog
  useEffect(() => {
    if (!supportsDialog) return;
    const d = dialogRef.current;
    if (!d) return;

    const onRequestClose = (ev: Event) => {
      try {
        const custom = ev as CustomEvent<{ openerId?: string; mode?: 'expanded' | 'fullscreen' }>;
        const openerId = custom?.detail?.openerId;
        if (openerId === idRef.current) return;
        if (internalSwitchingRef.current) return;

        if (d.open) {
          d.close();
        }
      } catch (e) {
        /* swallow */
      }
    };
    window.addEventListener('fullscreen-dialog-request-close', onRequestClose as EventListener);
    return () =>
      window.removeEventListener(
        'fullscreen-dialog-request-close',
        onRequestClose as EventListener,
      );
  }, [supportsDialog, dialogRef, idRef, internalSwitchingRef]);

  // Listener para cuando NO hay soporte de dialog (fallback)
  useEffect(() => {
    if (supportsDialog) return;
    const onRequestCloseFallback = (ev: Event) => {
      try {
        const custom = ev as CustomEvent<{ openerId?: string }>;
        const openerId = custom?.detail?.openerId;
        if (openerId === idRef.current) return;
        if (typeof onRequestClose === 'function') {
          onRequestClose();
        } else {
          window.dispatchEvent(
            new CustomEvent('fullscreen-dialog-request-close-fallback', {
              detail: { openerId: idRef.current },
            }),
          );
        }
      } catch {}
    };

    window.addEventListener(
      'fullscreen-dialog-request-close',
      onRequestCloseFallback as EventListener,
    );
    return () =>
      window.removeEventListener(
        'fullscreen-dialog-request-close',
        onRequestCloseFallback as EventListener,
      );
  }, [supportsDialog, onRequestClose, idRef]);
};

/**
 * Hook para manejar la apertura/cierre del diálogo nativo
 */
export const useDialogOpenClose = (
  supportsDialog: boolean,
  dialogRef: React.RefObject<HTMLDialogElement | null>,
  idRef: React.MutableRefObject<string>,
  internalSwitchingRef: React.MutableRefObject<boolean>,
  showExpanded?: boolean,
  showFullScreen?: boolean,
) => {
  useEffect(() => {
    if (!supportsDialog) return;
    const d = dialogRef.current;
    if (!d) return;

    const dispatchCloseRequest = (mode: 'expanded' | 'fullscreen') => {
      window.dispatchEvent(
        new CustomEvent('fullscreen-dialog-request-close', {
          detail: { openerId: idRef.current, mode },
        }),
      );
    };

    if (showFullScreen) {
      dispatchCloseRequest('fullscreen');

      if (d.open) {
        try {
          internalSwitchingRef.current = true;
          d.close();
        } catch {}
      }

      setTimeout(() => {
        try {
          internalSwitchingRef.current = false;
          d.showModal();
        } catch {
          d.setAttribute('open', 'true');
          internalSwitchingRef.current = false;
        }
      }, 0);
      return;
    }

    if (showExpanded) {
      dispatchCloseRequest('expanded');

      if (!d.open) {
        setTimeout(() => {
          try {
            d.showModal();
          } catch {
            d.setAttribute('open', 'true');
          }
        }, 0);
      }
      return;
    }

    // ni expand ni fullscreen -> cerrar si está abierto
    if (d.open) d.close();
  }, [showExpanded, showFullScreen, supportsDialog, dialogRef, idRef, internalSwitchingRef]);
};

